import React, { useCallback, useMemo } from 'react';
import type { InputNode, ReportListItem } from '../types';
import { ChipRow } from './MultiVariableNode';
import { RequiredMark } from './RequiredMark';
import type { ReportListFetcher } from './reportDropdownShared';
import type { ResolveNameOutcome } from '../hooks/useReportAnalyze';

type Props = {
  node: InputNode;
  value: string[] | undefined;
  onChange: (key: string, value: string[]) => void;
  disabled?: boolean;
  error?: string;
  reportListFetch?: ReportListFetcher;
  onReportGoGenerate?: (agentType: string | number) => void;
  onViewReport?: (item: ReportListItem) => void;
  hideCurrentSessionReport?: boolean;
  /** 已解析的报告名，按 `${key}_${idx}` 索引 */
  reportNames?: Record<string, string>;
  /** slot 失焦若为 URL → 解析报告名（itemKey, url, nodeType）→ outcome */
  onResolveName?: (
    itemKey: string,
    url: string,
    nodeType: 'multi-variable' | 'multi-variable-select',
  ) => Promise<ResolveNameOutcome>;
  /** name chip 清除（itemKey） */
  onClearReport?: (itemKey: string) => void;
  /** 清空槽位（splice 前移/尾部截断）时同步重排 reportNames（nodeKey, removedIdx） */
  onShiftReportNames?: (nodeKey: string, removedIdx: number) => void;
  /** 选报告后触发报告模块解析（V1：multi-variable-select 选中后额外 reportAnalyze） */
  onAnalyzeReport?: (itemKey: string, url: string) => void;
};

const normalizeTooltip = (t: InputNode['tooltip']): string => {
  if (!t) return '';
  return typeof t === 'string' ? t : JSON.stringify(t);
};

/**
 * V1 parity (RichTextEditor/nodes/multi-variable-select/render.ts):
 * 跟 multi-variable 渲染完全一样，仅 nodeType 不同（dropdown 接口透传 nodeType）。
 */
export const MultiVariableSelectNode: React.FC<Props> = React.memo(
  ({
    node,
    value,
    onChange,
    disabled,
    error,
    reportListFetch,
    onReportGoGenerate,
    onViewReport,
    hideCurrentSessionReport,
    reportNames,
    onResolveName,
    onClearReport,
    onShiftReportNames,
    onAnalyzeReport,
  }) => {
    const max = node.maxCount ?? 9;
    const min = node.minCount ?? 1;
    const tooltip = normalizeTooltip(node.tooltip);
    // V1 严格条件：reportListFetch + tooltip 双成立才显示 dropdown icon
    const showDropdown = !!reportListFetch && !!tooltip;
    const showInfoIcon = !showDropdown && !!tooltip;

    const arr = useMemo(() => {
      const raw = value ?? [];
      const lastNonEmpty = raw.reduce(
        (acc, cur, i) => (cur && cur.trim() ? i : acc),
        -1,
      );
      return raw.slice(0, lastNonEmpty + 1);
    }, [value]);

    const rows = useMemo(() => {
      const nonEmpty = arr.filter((v) => v && v.trim().length > 0);
      const target = Math.max(min, Math.min(nonEmpty.length + 1, max));
      const len = Math.min(Math.max(arr.length, target), max);
      return Array.from({ length: len }, (_, i) => arr[i] ?? '');
    }, [arr, min, max]);

    const update = useCallback(
      (idx: number, v: string) => {
        const next = [...rows];
        next[idx] = v;
        // 与 MultiVariableNode 一致：清空某槽时①中间空洞前移补位②裁掉尾部空串，
        // 否则 value 残留空串撑大 arr.length → 删 name chip / 清文本后残留删不掉的空框。
        if (v.trim() === '') {
          const lastNonEmpty = next.reduce(
            (acc, cur, i) => (cur && cur.trim() ? i : acc),
            -1,
          );
          if (idx < lastNonEmpty) {
            next.splice(idx, 1);
          }
          next.length = lastNonEmpty + 1;
          // 与 MultiVariableNode 一致：数组前移/截断后同步重排 reportNames 键，避免报告名串槽。
          onShiftReportNames?.(node.key!, idx);
        }
        onChange(node.key!, next);
      },
      [rows, node.key, onChange, onShiftReportNames],
    );

    const handleSelectReport = useCallback(
      (idx: number, item: ReportListItem) => {
        const next = [...rows];
        next[idx] = item.url;
        onChange(node.key!, next);
        // V1 parity：multi-variable-select 选报告后额外触发 reportAnalyze（数据副作用，
        // 不渲染模块——V1 effectiveConfig 只对 report-url/markdown(-name) 展开 module）。
        onAnalyzeReport?.(`${node.key}_${idx}`, item.url);
        // 选报告后触发 resolveName 获取报告名，使 chip 切到 name chip 模式
        if (onResolveName && /^https?:\/\//i.test(item.url)) {
          onResolveName(`${node.key}_${idx}`, item.url, 'multi-variable-select');
        }
      },
      [rows, node.key, onChange, onAnalyzeReport, onResolveName],
    );

    // name chip 清除：清空 slot 值。update 内部 onShiftReportNames 已处理名字丢弃+重排，
    // 不再额外调 onClearReport（避免按旧 idx 二次删除误伤前移过来的邻居名字）。
    const handleClearSlot = useCallback(
      (idx: number) => {
        update(idx, '');
      },
      [update],
    );

    return (
      <RequiredMark node={node}>
      <span
        className={`rte-multi-variable-select ${error ? 'rte-multi-variable-select-error' : ''}`}
      >
        {rows.map((row, idx) => {
          const itemKey = `${node.key}_${idx}`;
          return (
            <ChipRow
              key={idx}
              nodeKey={node.key!}
              idx={idx}
              row={row}
              placeholder={node.placeholder}
              disabled={disabled}
              tooltip={tooltip}
              onUpdate={(v) => update(idx, v)}
              showDropdown={showDropdown}
              showInfoIcon={showInfoIcon}
              reportListFetch={reportListFetch}
              onReportGoGenerate={onReportGoGenerate}
              onViewReport={onViewReport}
              hideCurrentSessionReport={hideCurrentSessionReport}
              onSelectReport={(item) => handleSelectReport(idx, item)}
              nodeType="multi-variable-select"
              reportName={reportNames?.[itemKey]}
              onResolveName={
                onResolveName
                  ? (url) => onResolveName(itemKey, url, 'multi-variable-select')
                  : undefined
              }
              onClearName={onClearReport ? () => handleClearSlot(idx) : undefined}
            />
          );
        })}
      </span>
      </RequiredMark>
    );
  },
);
MultiVariableSelectNode.displayName = 'MultiVariableSelectNode';
